import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fromISO8601 } from "../../../../../utils/iso8601";
import { getOtherEditors } from "../roundabout-utilities";

import type { ISO8601 } from "../../../../../utils/iso8601";
import type { RoundaboutRoomUser } from "../roundabout-markings";

interface UseOtherStartedEditingProps {
  roomUsers: RoundaboutRoomUser[];
  mySub: string;
}

type OtherJustSaved = { saved: false } | { saved: true; nicknames: string[] };

export const makeLastDate = (value: ISO8601 | null): number => {
  if (value) {
    return fromISO8601(value).getTime();
  }
  return 0;
};
export const useOtherJustSaved = ({
  mySub,
  roomUsers,
}: UseOtherStartedEditingProps): {
  otherJustSaved: OtherJustSaved;
  onCloseOtherJustSaved: () => void;
} => {
  const [otherJustSaved, setOtherJustSaved] = useState<OtherJustSaved>({
    saved: false,
  });

  const onCloseOtherJustSaved = useCallback(() => {
    setOtherJustSaved({
      saved: false,
    });
  }, []);

  const otherEditors = useMemo(() => getOtherEditors(roomUsers, mySub), [roomUsers, mySub]);

  // Need to know when a person has last saved and compare that to this sync
  // separately to acknowledgments to keep the brain intact
  const otherEditorLastSavedAt = useRef<Record<string, number>>(
    otherEditors.reduce<Record<string, number>>((aggragator, current) => {
      aggragator[current.userId] = makeLastDate(current.momentFormLastSaveAt);
      return aggragator;
    }, {}),
  );

  useEffect(() => {
    // loop through otherEditors and see if any new save timestamps
    const nicknames: string[] = [];
    for (const { momentFormLastSaveAt, userId, nickname } of otherEditors) {
      if (!momentFormLastSaveAt) {
        continue;
      }
      const previousSessionLastSavedTime = otherEditorLastSavedAt.current[userId] || 0;
      const currentSessionLastSavedTime = fromISO8601(momentFormLastSaveAt).getTime();
      if (currentSessionLastSavedTime > previousSessionLastSavedTime) {
        // this user has saved since we last checked
        nicknames.push(nickname);
      }
      otherEditorLastSavedAt.current[userId] = currentSessionLastSavedTime;
    }
    if (nicknames.length > 0) {
      setOtherJustSaved({
        saved: true,
        nicknames,
      });
    }
  }, [otherEditors]);

  return {
    onCloseOtherJustSaved,
    otherJustSaved,
  };
};
